import { AppConfig } from "./AppConfig";
import { log } from "./logger";

/**
 * Получает base origin для URL
 * В browser окружении использует window.location.origin
 * В non-browser окружении (WeChat) использует AppConfig.apiBasePath
 */
export function getBaseOrigin(): string {
	if (typeof window !== 'undefined' && window.location) {
		log.debug("getBaseOrigin: using window.location.origin", window.location.origin);
		return window.location.origin;
	}
	// В WeChat или других non-browser окружениях используем apiBasePath
	log.debug("getBaseOrigin: using AppConfig.apiBasePath", AppConfig.apiBasePath);
	return AppConfig.apiBasePath;
}

/**
 * https://stackoverflow.com/a/58362609
 */
export const exitFullscreen = async () => {
	try {
		// As correctly mentioned in the accepted answer, exitFullscreen only works on document
		const cancellFullScreen =
			document.exitFullscreen ||
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(document as any).mozCancelFullScreen ||
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(document as any).webkitExitFullscreen ||
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(document as any).msExitFullscreen;
		log.info("Exiting fullscreen");
		await cancellFullScreen.call(document);
		log.info("Exited fullscreen");
	} catch (err) {
		// Тут будет ошибка дескать "мы не вышли из фулскрина потому-что мы не там - нам не важно
		// ignore
	}
};

export function errorToString(error: Error | unknown): string {
	if (!error) {
		return "Unknown error";
	}

	// If it's an Error instance
	if (error instanceof Error) {
		const errorMessage = error.message;

		// Handle case where error.message is an object
		if (typeof errorMessage === "object" && errorMessage !== null) {
			try {
				return JSON.stringify(errorMessage);
			} catch {
				// ignore
			}
		}

		// Handle string messages and add stack trace in development
		return process.env.NODE_ENV === "development" ? `${errorMessage}\n${error.stack || ""}` : errorMessage;
	}

	// Handle non-Error objects
	if (typeof error === "object") {
		try {
			return JSON.stringify(error);
		} catch {
			// ignore
		}
	}

	// Handle primitive values
	return String(error);
}

/**
 * Проверяет, является ли переданная строка абсолютным URL-адресом.
 */
const isAbsoluteURL = (url: string): boolean => {
	try {
		new URL(url);
		return true;
	} catch (_e) {
		return false;
	}
};

/**
 * Безопасно объединяет базовый URL (или путь) с дополнительным путем.
 * @param base - Базовый URL или путь
 * @param path - Дополнительный путь для объединения
 * @returns Новая строка с объединенным URL/путем
 */
export const joinURL = (base: string, path: string): string => {
	if (isAbsoluteURL(base)) {
		return new URL(path, base).toString();
	}

	const cleanedBase = base.replace(/\/$/, "");
	const cleanedPath = path.replace(/^\//, "");
	const result = `${cleanedBase}/${cleanedPath}`;

	if (result.startsWith("//")) {
		return result.substring(1);
	}

	return result;
};

/**
 * Находит конфигурацию адаптера по типу
 * @param adapters - Массив адаптеров
 * @param adapter - Тип адаптера для поиска
 * @returns Конфигурация адаптера или undefined
 */
export function findAdapterConfig<T extends { IframeType?: string }>(
	adapters: T[] | undefined | null,
	adapter?: string
): T | undefined {
	if (!adapter || !adapters) {
		return undefined;
	}
	return adapters.find((el) => el.IframeType === adapter);
}

/**
 * Карта существующих адаптеров с документацией
 */
const EXISTING_ADAPTERS_MAP = new Map([
	["fxid", true],
	["ok", true],
	["yandex", true]
]);

/**
 * Получает URL документа для указанного адаптера
 * @param params - Параметры: docFile и adapter
 * @param baseUrl - Базовый URL приложения
 * @param language - Язык документа (ru или en)
 * @returns URL документа
 */
export function getDocUrl(params: {
	docFile: string;
	adapter?: string | null
}, baseUrl: string, language: string): URL {
	const adapterParam = params.adapter ?? "fxid";

	const isAbsoluteUrl = baseUrl.startsWith('http');
	const origin = getBaseOrigin();
	log.debug("getDocUrl: origin =", origin, "baseUrl =", baseUrl, "isAbsoluteUrl =", isAbsoluteUrl);

	const docsPath = isAbsoluteUrl
		? `${baseUrl}/docs`
		: `${origin}${baseUrl}/docs`;

	const lang = language === "ru" ? "ru" : "en";
	const adapter = EXISTING_ADAPTERS_MAP.get(adapterParam) ? adapterParam : "fxid";

	const fullPath = `${docsPath}/${adapter}/${lang}/${params.docFile}`;
	log.debug("getDocUrl: constructing URL with fullPath =", fullPath);

	return new URL(fullPath);
}
